import {ContentPageTableHeader} from "../tables/ContentPageTableHeader";
import {ContentPageTable} from "../tables/ContentPageTable";
import {FormDialog} from "../dialogs/FormDialog";
import React, {ReactNode, useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {useAxios} from "../../technical/rest/AxiosProvider";
import {EntityRef} from "../../technical/rest/entities";
import {CrudApi} from "../../technical/rest/restApi";
import {PaginatedContentPageTable, TableOptions} from "../tables/PaginatedContentPageTable";
import {MUIDataTableColumn} from "mui-datatables";
import {Alert, Snackbar} from "@mui/material";
import {WithRestrictedEdit} from "../../technical/security/WithRestrictedEdit";
import {generatePath, useNavigate, useParams} from "react-router";
import {routePaths} from "../router/routes";
import {isCustomValidationErrorResponse, isSpringValidationErrorResponse} from "../../technical/rest/errorHandling";

export type EditForm<T> = (
  entity: T,
  handleSave: (entity: T) => Promise<T>,
  handleDelete: (entity: T) => Promise<void>,
  handleClose: (event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => void,
  handleRefresh: () => void
) => ReactNode

export function EntityListAndEditPage<LIST_TYPE extends EntityRef, DETAILED_TYPE extends EntityRef>(props: {
  crudApi: CrudApi<LIST_TYPE, DETAILED_TYPE>,
  columns: MUIDataTableColumn[],
  defaultEntity: (t: TFunction) => DETAILED_TYPE,
  additionalHeaderItem?: React.ReactElement
  i18nPrefix: string,
  editForm: EditForm<DETAILED_TYPE>,
  tableOptions?: TableOptions,
  title?: React.ReactNode,
  onEntityQueryParamsChange?: (params: string) => void,
  additionalQueryParams?: string,
  aboveTable?: ReactNode,
  onTotalCountChange?: (count: number) => void,
  /**
   * Optional notice shown after an entity was saved, e.g. when the server adjusted the saved values
   */
  saveNotice?: (saved: DETAILED_TYPE) => ReactNode | undefined,
} & WithRestrictedEdit) {
  const {t} = useTranslation();
  const axios = useAxios();
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [entities, setEntities] = useState<LIST_TYPE[]>([]);
  const [actualEntity, setActualEntity] = useState<DETAILED_TYPE>(props.defaultEntity(t))
  const [dataVersion, setDataVersion] = useState(0);
  const [saveErrors, setSaveErrors] = useState<string>();
  const [saveNotice, setSaveNotice] = useState<ReactNode>();
  const navigate = useNavigate();
  const params = useParams();

  const onTotalCountChange = props.onTotalCountChange;
  const refreshList = useCallback(() => {
    if (props.crudApi.queryList) {
      props.crudApi.queryList(axios, props.additionalQueryParams).then(loaded => {
        setEntities(loaded);
        onTotalCountChange?.(loaded.length);
      });
    }
    if (props.crudApi.queryPage) {
      setDataVersion(curr => curr + 1);
    }
  }, [axios, props.crudApi, props.additionalQueryParams, onTotalCountChange]);

  useEffect(() => {
    refreshList();
  }, [axios, props.crudApi, refreshList])

  useEffect(() => {
    if (params.id) {
      props.crudApi.queryOne(axios, params.id).then(
        (detailedEntity) => {
          setActualEntity(detailedEntity);
          setShowFormDialog(true);
        }
      )
    }
  }, [params, axios, props.crudApi]);

  function handleAddButtonClick() {
    setActualEntity(props.defaultEntity(t));
    setShowFormDialog(true);
  }

  function handleRowClick(entity: LIST_TYPE) {
    if (entity.id === undefined) {
      return;
    }
    navigateToEntityDetailsPage(entity.id)
  }

  async function navigateToEntityDetailsPage(id: string) {
    const detailedEntity: DETAILED_TYPE = await props.crudApi.queryOne(axios, id);
    setActualEntity(detailedEntity);
    const newPath = generatePath(routePaths.userAndDataWithSelectionAndId, {selection: params.selection, id: id})
    navigate(newPath)
  }

  function handleCloseDialog(event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') {
    if (reason === 'backdropClick') {
      console.info('backdrop click');
      return;
    }
    closeDialog()
  }

  function closeDialog() {
    setShowFormDialog(false);
    const newPath = generatePath(routePaths.userAndDataWithSelection, {selection: params.selection})
    navigate(newPath)
  }

  async function handleSave(entity: DETAILED_TYPE) {
    if (entity.id) {
      try {
        const updated = await props.crudApi.updateOne(axios, entity);
        setSaveNotice(props.saveNotice?.(updated));
        closeDialog()
        refreshList();
        return updated
      } catch (error) {
        console.error('Could not update entity', error);
        reportSaveError(error);
        return Promise.reject(error);
      }
    } else {
      try {
        const created = await props.crudApi.createOne(axios, entity);
        setSaveNotice(props.saveNotice?.(created));
        closeDialog()
        refreshList();
        return created
      } catch (error) {
        console.error('Could not create entity', error);
        reportSaveError(error);
        return Promise.reject(error);
      }
    }
  }

  /**
   * Validation errors belong to the form (which shows them at the affected field), everything else is
   * reported as a general error here. In both cases the dialog stays open so nothing typed in gets lost.
   */
  function reportSaveError(error: any) {
    if (!isCustomValidationErrorResponse(error) && !isSpringValidationErrorResponse(error)) {
      setSaveErrors(error + '');
    }
  }

  async function handleDelete(entity: DETAILED_TYPE) {
    if (!entity.id) {
      return Promise.resolve()
    }
    try {
      await props.crudApi.deleteOne(axios, entity.id)
      closeDialog()
      refreshList();
    } catch (error) {
      console.error('Could not delete entity', error);
      closeDialog()
      refreshList();
    }
  }


  return (
    <>
      <ContentPageTableHeader onClick={handleAddButtonClick}
                              title={props.title ?? t(props.i18nPrefix + '.titleLong')}
                              titleButton={t(props.i18nPrefix + '.buttonCreate')}
                              additionalHeaderItem={props.additionalHeaderItem}
                              isEditable={props.isEditable}
                              sx={{marginTop: 2}}
      />
      {props.aboveTable}
      {props.crudApi.queryList &&
        <ContentPageTable entities={entities} columns={props.columns} handleRowClick={handleRowClick}
                          tableOptions={props.tableOptions}/>}
      {props.crudApi.queryPage &&
        <PaginatedContentPageTable pagedQuery={props.crudApi.queryPage} columns={props.columns} handleRowClick={handleRowClick}
                                   dataVersion={dataVersion} tableOptions={props.tableOptions}
                                   onParamsChange={props.onEntityQueryParamsChange}
                                   onTotalCountChange={props.onTotalCountChange}
                                   additionalQueryParams={props.additionalQueryParams}/>}
      <FormDialog open={showFormDialog} handleClose={handleCloseDialog}>
        {props.editForm(actualEntity, handleSave, handleDelete, handleCloseDialog, refreshList)}
      </FormDialog>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={!!saveErrors}
        autoHideDuration={10000}
        onClose={() => setSaveErrors(undefined)}
      >
        <Alert severity={'error'}>{saveErrors}</Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={!!saveNotice}
        onClose={(event, reason) => reason !== 'clickaway' && setSaveNotice(undefined)}
      >
        <Alert severity={'warning'} onClose={() => setSaveNotice(undefined)}>{saveNotice}</Alert>
      </Snackbar>
    </>
  )
}
